import { useQuery } from "@tanstack/react-query";
import { DashboardsService, type GetApiDashboardsResponse } from "@/lib/api_client";
import { client } from "@/lib/api_client/client.gen";
import { getAuthHeaders } from "@/lib/api";

export type DashboardMeta = GetApiDashboardsResponse[number];
export type DashboardChartMeta = DashboardMeta["charts"][number];

export function useDashboards() {
  return useQuery({
    queryKey: ["dashboards"],
    queryFn: async ({ signal }) => {
      const res = await DashboardsService.getApiDashboards({
        headers: getAuthHeaders(),
        signal,
      });
      if (!res.data) throw new Error("Kunne ikke hente dashboards");
      return res.data;
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Generic per-chart runner. The backend registers one POST endpoint per
 * (dashboard, chart) pair (e.g. `/api/dashboards/discount-codes/code-totals/run`).
 *
 * We intentionally don't call the per-chart generated `DashboardsService.*`
 * methods so the same hook works for any future dashboard without per-chart
 * wiring — adding a new dashboard becomes one backend file + one frontend
 * component, no hook changes. We do, however, route through the same
 * `@hey-api/client-fetch` instance the generated services use, so the
 * shared 401 interceptor (in `lib/setup-interceptors.ts`) fires consistently.
 *
 * Trade-off: chart params/rows aren't typed against the OpenAPI spec at this
 * seam; the bespoke dashboard component declares the shapes.
 */
export function useDashboardChart<TParams extends object, TRow = Record<string, unknown>>(
  dashboardId: string,
  chartId: string,
  params: TParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["dashboard-chart", dashboardId, chartId, params],
    queryFn: ({ signal }) =>
      runDashboardChart<TRow>(dashboardId, chartId, params, signal),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

/**
 * One chart run, without React Query wrapped around it.
 *
 * Split out of `useDashboardChart` so a component can also run a chart on
 * demand rather than as render state — the leads export re-runs its segment at
 * a higher row cap when the download button is pressed, which is an action, not
 * something the page is continuously displaying.
 */
export async function runDashboardChart<TRow = Record<string, unknown>>(
  dashboardId: string,
  chartId: string,
  params: object,
  signal?: AbortSignal
): Promise<TRow[]> {
  // `@hey-api/client-fetch`'s TS types claim it unwraps single-key response
  // wrappers (`{ rows: ... }` → `data: rows`), but the runtime never unwraps.
  // Pass `unknown` and cast — the generated SDK clients hit the same quirk.
  const res = await client.post<unknown>({
    url: `/api/dashboards/${dashboardId}/${chartId}/run`,
    body: params,
    headers: getAuthHeaders(),
    signal,
  });
  const body = res.data as { rows: TRow[] } | undefined;
  if (!body) {
    const errMsg =
      (res.error as { error?: string } | undefined)?.error ??
      `Kunne ikke kjøre ${dashboardId}/${chartId}`;
    throw new Error(errMsg);
  }
  return body.rows;
}
